/**
 * Перенос `presentation/providers/chat_controller/chat_message.dart`.
 *
 * Dart-иерархия `sealed class ChatMessage` -> `MyMessage | AssistantMessage`
 * перенесена размеченным объединением по `role`: `switch` и сужение типа
 * работают так же, как `switch` по sealed-классу, а объекты остаются
 * неизменяемыми (в Dart они и так были без сеттеров).
 *
 * Сообщения создаются только фабриками [createMyMessage] и
 * [createAssistantMessage]: в них живёт сквозной счётчик `_counter++`
 * из Dart и вычисляются производные поля `isMy` и `hasError`.
 */

import type { ContextChunkModel } from '@/domain/models/contextChunkModel';
import { resolveNullable, type Nullable } from '@/utils/nullable';

export const ChatRole = {
  user: 'user',
  assistant: 'assistant',
} as const;

export type ChatRole = (typeof ChatRole)[keyof typeof ChatRole];

/**
 * `static int _counter = 0` из `ChatMessage`. Счётчик один на модуль —
 * как статическое поле класса в Dart: нумерация сквозная по всем
 * сообщениям, а не по каждому виду отдельно.
 */
let messageCounter = 0;

/** `id ?? _counter++`. */
function nextMessageId(): number {
  const id = messageCounter;
  messageCounter += 1;
  return id;
}

interface ChatMessageBase {
  readonly id: number;
  readonly role: ChatRole;
  readonly message: string;

  /**
   * `bool get isMy => role == ChatRole.user`.
   *
   * В Dart это геттер, здесь — поле литерального типа: фабрика ставит его
   * один раз, зато обращения из виджетов (`message.isMy`) переносятся
   * дословно, и TypeScript по нему сужает тип не хуже, чем по `role`.
   */
  readonly isMy: boolean;
}

/** `class MyMessage extends ChatMessage` — вопрос посетителя. */
export interface MyMessage extends ChatMessageBase {
  readonly role: typeof ChatRole.user;
  readonly isMy: true;

  /** `final File? audio` — запись с микрофона; у набранного текста её нет. */
  readonly audio: File | null;
}

/** `class AssistantMessage extends ChatMessage` — ответ аватара. */
export interface AssistantMessage extends ChatMessageBase {
  readonly role: typeof ChatRole.assistant;
  readonly isMy: false;

  readonly errorMessage: string | null;

  readonly chunks: readonly ContextChunkModel[];

  /**
   * `bool get hasError => errorMessage != null`. Поле производное, но
   * пересчитывается в фабрике, через которую проходит и `copyWith`, —
   * разъехаться с `errorMessage` оно не может.
   */
  readonly hasError: boolean;
}

export type ChatMessage = MyMessage | AssistantMessage;

/** Аргументы конструктора `MyMessage`. */
export interface MyMessageInit {
  readonly id?: number;
  readonly audio: File | null;
  readonly message: string;
}

export function createMyMessage({ id, audio, message }: MyMessageInit): MyMessage {
  return {
    id: id ?? nextMessageId(),
    role: ChatRole.user,
    isMy: true,
    message,
    audio,
  };
}

/** Аргументы конструктора `AssistantMessage`. */
export interface AssistantMessageInit {
  readonly id?: number;
  readonly message: string;
  /** `this.chunks = const []`. */
  readonly chunks?: readonly ContextChunkModel[];
  readonly errorMessage?: string | null;
}

export function createAssistantMessage({
  id,
  message,
  chunks = [],
  errorMessage = null,
}: AssistantMessageInit): AssistantMessage {
  return {
    id: id ?? nextMessageId(),
    role: ChatRole.assistant,
    isMy: false,
    message,
    chunks,
    errorMessage,
    hasError: errorMessage !== null,
  };
}

/**
 * `Map<String, dynamic> toJson()` — тело запроса к LLM.
 * `role.name` в Dart давал 'user'/'assistant'; здесь это само значение
 * перечисления, так что строка та же.
 */
export function chatMessageToJson(message: ChatMessage): Record<string, unknown> {
  return {
    role: message.role,
    content: message.message,
  };
}

/**
 * Патч для [assistantMessageCopyWith].
 *
 * `errorMessage` обёрнут в `Nullable` — это тот же sentinel, что в Dart
 * (`Nullable<String?>? errorMessage`): без обёртки поле не трогается,
 * `new Nullable(null)` стирает ошибку.
 */
export interface AssistantMessagePatch {
  readonly message?: string;
  readonly errorMessage?: Nullable<string | null>;
  readonly chunks?: readonly ContextChunkModel[];
}

/** `AssistantMessage copyWith(...)` — всегда новый объект, `id` сохраняется. */
export function assistantMessageCopyWith(
  message: AssistantMessage,
  patch: AssistantMessagePatch,
): AssistantMessage {
  return createAssistantMessage({
    id: message.id,
    message: patch.message ?? message.message,
    chunks: patch.chunks ?? message.chunks,
    errorMessage: resolveNullable(patch.errorMessage, message.errorMessage),
  });
}
